import { Component, Input, Output, EventEmitter, ElementRef, OnInit, OnDestroy, OnChanges, SimpleChanges, ViewChild, forwardRef, ChangeDetectorRef, AfterViewInit } from "@angular/core";
import { SlickDropDownService } from "./slick-drop-down.service";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { SlickSleepService } from "../utils/slick-sleep.service";
import { SlickUtilsService } from "../utils/slick-utils.service";
import { SlickInitService } from "../utils/slick-init.service";
import { SlickLogService } from "../utils/slick-log.service";
import { ISlickDropDownButtonModel } from "./slick-drop-down-button.model";

export enum SlickDropDownSearchTypes { startsWith, eachWord, any }

// The phone picker shows a search box once a list is longer than this
const PICKER_SEARCH_MIN_ITEMS = 10;

@Component({
	selector: 'slick-drop-down',
	templateUrl: 'slick-drop-down.component.html',
	providers: [SlickDropDownService, SlickLogService,
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => SlickDropDownComponent),
			multi: true,
		}]
})
export class SlickDropDownComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy, ControlValueAccessor {
	@Input() placeholder: string = '';
	@Input() items: any[];
	@Input() idFieldName: string = "id";
	@Input() textFieldName: string = "text";
	@Input() compact: boolean = false;
	@Input() disabled: boolean = false;
	@Input() allowEmpty: boolean = true;
	@Input() getUrl: string;
	@Input() width: string = '100%';
	@Input() listWidth: string = '100%';
	@Input() height: string = '300px';
	@Input() showLoadingMessage: boolean = false;
	@Input() showDebug: boolean;
	@Input() tabindex: number;
	@Input() validationIndicator: boolean;
	@Input() validationIndicatorType: string;
	@Input() attachTo: string;
	@Input() searchType: SlickDropDownSearchTypes = SlickDropDownSearchTypes.eachWord;
	@Input() icon: string;
	@Input() isMobile: boolean = false;
	@Input() cssClass: string;

	@Output() onExpand: EventEmitter<any> = new EventEmitter<any>();
	@Output() onSelect: EventEmitter<any> = new EventEmitter<any>();
	@Output() onChange: EventEmitter<any> = new EventEmitter<any>();

	@ViewChild("containerDiv") containerDiv: ElementRef;
	@ViewChild("dropdownList") dropdownList: ElementRef;
	@ViewChild("selectedTextInputRef") selectedTextInputRef: ElementRef;
	@ViewChild("mobilePickerRef") mobilePickerRef: ElementRef;
	@ViewChild("mobilePickerListRef") mobilePickerListRef: ElementRef;

	// This is necessary so that the scroll and resize events can add and remove the event properly,
	// as well as getting the correct ES6 function
	fnReposition = () => this.reposition();
	fnDocumentClick = (e) => this.documentClick(e);
	uuid: string;
	selectedId: string;
	selectedText: string;
	expanded: boolean = false;
	selectedItem: any;
	selectedIndex: number;
	filterText: string = '';
	visibleItems: any[];
	left: number;
	top: number;
	listGroupWidth: string;
	isVisible: boolean = false;
	isAndroid: boolean = ((SlickInitService.getPlatform() || '').toLowerCase() === 'android');
	pickerOpen: boolean = false;

	// The mobile app turns this on for every dropdown. Typing to search meant every tap on a phone
	// brought up the keyboard, which covered the list. (The isMobile input is older and unused.)
	get mobilePicker(): boolean {
		return SlickInitService.getParams().isMobile === true;
	}

	get showPickerSearch(): boolean {
		return (this.items?.length ?? 0) > PICKER_SEARCH_MIN_ITEMS;
	}



	constructor(private slickDropDownService: SlickDropDownService,
		private changeDetector: ChangeDetectorRef) {
		this.uuid = SlickUtilsService.newGuid();
	}

	async ngOnInit() {
		this.attachTo = this.attachTo || SlickInitService.getParams().attachTo || 'body';
	}

	ngAfterViewInit() {
		if (this.cssClass)
			(<HTMLDivElement>this.containerDiv.nativeElement).classList.add(...this.cssClass.split(" "));
	}

	propagateChange = (_: any) => { };

	// this is the initial value set to the component
	public writeValue(obj: any) {
		if (obj !== null && obj !== undefined)
			this.selectedId = obj;
		else
			this.selectedId = null;

		this.selectItem(this.selectedId, false);
		this.collapse();
	}

	// registers 'fn' that will be fired when changes are made
	// this is how we emit the changes back to the form
	public registerOnChange(fn: any) {
		this.propagateChange = fn;
	}

	// not used, used for touch input
	public registerOnTouched() { }

	async ngOnChanges(changes: SimpleChanges) {
		await SlickSleepService.sleep();

		if (changes.items) {
			this.visibleItems = this.slickDropDownService.getVisibleItems(this.items, this.idFieldName, this.textFieldName);
			this.selectItem(this.selectedId, false);
			if (this.selectedTextInputRef && document.activeElement === this.selectedTextInputRef.nativeElement)
				this.expand();
		}

		if (changes.showLoadingMessage) {
			await SlickSleepService.sleep();
			if (this.showLoadingMessage === true)
				this.selectedText = "Loading...";
		}

		if (changes.getUrl) {
			if (this.showLoadingMessage === true) {
				this.selectedText = "Loading...";
			}

			let items: any[] = await this.slickDropDownService.getItemsFromServer(this.getUrl)
			this.selectedText = null;
			this.items = items
			this.visibleItems = this.slickDropDownService.getVisibleItems(this.items, this.idFieldName, this.textFieldName);
			this.selectItem(this.selectedId, false);
			if (document.activeElement === this.selectedTextInputRef.nativeElement)
				this.expand();
		}

		if (changes.validationIndicator && changes.validationIndicator.firstChange) {
			if (!this.validationIndicatorType)
				this.validationIndicatorType = "error";
		}

		if (changes.allowEmpty)
			this.allowEmpty = (this.allowEmpty.toString().toLowerCase() === 'false') ? false : true;

		if (changes.isMobile)
			this.isMobile = (this.isMobile.toString().toLowerCase() === 'true') ? true : false;

		if (changes.disabled && changes.disabled.currentValue)
			this.disabled = (changes.disabled.currentValue.toString().toLowerCase() !== 'false');

		if (changes.compact && changes.compact.currentValue)
			this.compact = (changes.compact.currentValue.toString().toLowerCase() !== 'false');

		if (changes.searchType) {
			if (changes.searchType.currentValue.toString().toLowerCase() === 'eachword')
				this.searchType = SlickDropDownSearchTypes.eachWord;
			else if (changes.searchType.currentValue.toString().toLowerCase() === 'any')
				this.searchType = SlickDropDownSearchTypes.any;
			else
				this.searchType = SlickDropDownSearchTypes.startsWith;
		}
	}

	ngOnDestroy() {
		this.collapse();
		document.removeEventListener("click", this.fnDocumentClick, true);

		// it's okay if this fails.  It was already removed
		try {
			document.body.removeChild(this.dropdownList.nativeElement);
		}
		catch { }

		// The picker lives on <body>, so leaving the page (Android back button) would otherwise leave it there
		if (this.pickerOpen)
			SlickUtilsService.removeElement(this.mobilePickerRef?.nativeElement);
	}

	async setFocus(showDropdownList: boolean = false) {
		this.filterText = '';
		this.visibleItems = this.slickDropDownService.getVisibleItems(this.items, this.idFieldName, this.textFieldName);

		// If this is true, just set expanded to false to force an expand
		if (showDropdownList === true)
			this.expanded = false;

		await SlickUtilsService.waitForElement("#slick-drop-down_" + this.uuid);
		(<HTMLInputElement>this.selectedTextInputRef.nativeElement).focus();
	}

	private async expand() {
		if (this.disabled)
			return;

		if (this.mobilePicker) {
			this.openPicker();
			return;
		}

		this.isVisible = true;

		this.changeDetector.detectChanges();

		SlickUtilsService.attachElement(this.dropdownList.nativeElement, this.attachTo)

		this.reposition();

		await SlickSleepService.sleep();
		this.dropdownList.nativeElement.style.display = "inline-block";

		const selectedItems = this.dropdownList.nativeElement.getElementsByClassName("active");
		if (selectedItems && selectedItems.length > 0)
			(<HTMLElement>selectedItems[0]).scrollIntoView({ block: 'nearest' });

		this.expanded = true;


		// This is necessary, otherwise `this` in the reposition function will be window
		// and not the component.
		// Since angular has no way to use the third parameter, I'm doing this in raw js
		document.addEventListener("click", this.fnDocumentClick, true);

		if (this.onExpand)
			this.onExpand.emit();
	}

	private collapse() {
		if (this.expanded === false)
			return;

		this.filterText = '';
		this.visibleItems = this.slickDropDownService.getVisibleItems(this.items, this.idFieldName, this.textFieldName);
		this.selectedText = this.selectedItem ? this.selectedItem.text : null; 
		this.expanded = false;
		this.dropdownList.nativeElement.style.display = "none";
		document.removeEventListener("click", this.fnDocumentClick, true);
		SlickUtilsService.removeElement(this.dropdownList.nativeElement);
		this.isVisible = false;
	}

	private reposition() {
		const dropDownRect = this.containerDiv.nativeElement.getBoundingClientRect();
		const parentRect = (this.attachTo === 'body') ? null : document.body.querySelector(this.attachTo).getBoundingClientRect();

		const containerLeft = (this.attachTo === 'body') ? dropDownRect.left + window.scrollX : (dropDownRect.left - parentRect.left);
		const containerTop = (this.attachTo === 'body') ? dropDownRect.top + window.scrollY : (dropDownRect.top - parentRect.top);

		const containerHeight = this.containerDiv.nativeElement.offsetHeight;
		const containerWidth = dropDownRect.width;

		this.width = containerWidth;
		if (this.listWidth === '100%')
			this.listGroupWidth = this.width;
		else if (this.listWidth === 'auto')
			this.listGroupWidth = "auto";
		else
			this.listGroupWidth = this.listWidth;
		this.top = (containerTop + containerHeight);
		this.left = containerLeft;
	}

	private async documentClick(e: MouseEvent) {
		// Not entirely sure why, but iPhones call onFocus when clicking out of the dropdown
		if (!this.expanded || !e.target)
			return;

		if (SlickUtilsService.checkParentIdExists(<HTMLElement>e.target, "slick-drop-down_" + this.uuid) === true)
			return;

		(<HTMLInputElement>this.selectedTextInputRef.nativeElement).setSelectionRange(0, 0);
		if (this.selectedIndex !== null && this.visibleItems.length > 0 && this.selectedIndex < this.visibleItems.length) {
			let itemId = this.visibleItems[this.selectedIndex].id;
			this.selectItem(itemId, true);
		}
		

		this.collapse();
	}

	private selectItem(id: string | number, emitOnSelect: boolean) {
		let item = this.slickDropDownService.getSelectedItem(this.visibleItems, id);

		if (item) {
			this.selectedItem = item;
			this.selectedId = item.id;
			this.selectedText = item.text;
			this.selectedIndex = this.slickDropDownService.getItemIndex(this.visibleItems, this.selectedId);

			if (this.onChange)
				this.onChange.emit(this.selectedItem.item);
			if (emitOnSelect === true && this.onSelect)
				this.onSelect.emit(this.selectedItem.item);
			this.propagateChange(this.selectedId);
		}
		else {
			this.selectedItem = null;
			this.selectedText = null;
			this.selectedIndex = null;
		}
	}

	async onKeyDown(e: KeyboardEvent) {
		if (this.isAndroid)
			return;

		// Tab/enter
		if (e.which === 9 || e.which === 13) {
			this.collapse();

			if (this.selectedIndex === null || this.selectedIndex === undefined)
				return;

			(<HTMLInputElement>this.selectedTextInputRef.nativeElement).setSelectionRange(0, 0);
			let itemId = this.visibleItems[this.selectedIndex].id;
			this.selectItem(itemId, true);

			return true;
		}

		if (e.key && e.key.length > 1)
			return true;

	}

	async onKeyUp(e: KeyboardEvent) {
		if (e.which !== 8)
			e.preventDefault();
		e.stopPropagation();

		// If this is android, it won't pass us any key data so just read the entire input element
		if (this.isAndroid === true) {
			await SlickSleepService.sleep();
			this.filterText = (<HTMLInputElement>this.selectedTextInputRef.nativeElement).value;
			this.filterSearch();
			return;
		}

		// Esc
		if (e.which === 27) {
			this.collapse();
			return false;
		}

		// Arrowup
		if (e.which === 38) {
			if (this.selectedIndex === null || this.selectedIndex === undefined)
				this.selectedIndex = 0;

			if (this.selectedIndex > 0)
				this.selectedIndex--;

			return false;
		}

		// Arrowdown
		if (e.which === 40) {
			if (this.selectedIndex === null || this.selectedIndex === undefined)
				this.selectedIndex = -1;

			if (!this.expanded || !this.visibleItems) {
				this.expand();
				return false;
			}

			if (this.selectedIndex < this.visibleItems.length - 1)
				this.selectedIndex++;

			return false;
		}

		// If F1 is pressed, key length will be 2
		if (e.key.length > 1 || e.ctrlKey || e.altKey)
			return false;

		return true;
	}

	private async filterSearch() {
		if (!this.expanded)
			this.expand();

		this.applyFilter();
		this.selectedIndex =  null;
	}

	// Narrows visibleItems to filterText. Shared by the typed search and the phone picker's search box.
	private applyFilter() {
		this.visibleItems = this.slickDropDownService.getVisibleItems(this.items, this.idFieldName, this.textFieldName);
		if (!this.visibleItems)
			return;

		if (this.searchType === SlickDropDownSearchTypes.startsWith) {
			this.visibleItems = this.visibleItems.filter(x => (x.text ?? '').toLowerCase().startsWith((this.filterText ?? '').toLowerCase()));
		}
		else if (this.searchType === SlickDropDownSearchTypes.eachWord) {
			this.visibleItems = this.visibleItems.filter(x => (" " + (x.text ?? '').toLowerCase()).indexOf(" " + (this.filterText ?? '').toLowerCase()) >= 0);
		}
		else if (this.searchType === SlickDropDownSearchTypes.any) {
			this.visibleItems = this.visibleItems.filter(x => (x.text ?? '').toLowerCase().includes((this.filterText ?? '').toLowerCase()));
		}
	}

	lastEvent: string = null;
	onDropdownClicked(e: MouseEvent) {
		if (this.mobilePicker) {
			this.openPicker();
			return;
		}

		// This is some hacky code that only allows 1 event every 100ms
		// This is because we don't know if they will tab into the focus or click into it.  If they tab, we only get 1 focus event
		// if they click we get focus AND click.
		if (this.lastEvent !== null)
			return;

		this.lastEvent = e.type;

		// If this is the focus event, we may or may not get
		// a click event after.  If it is, wait a bit longer for
		// slower computers to fire the click
		if (this.lastEvent === 'focus')
			setTimeout(() => this.lastEvent = null, 500);
		else
			setTimeout(() => this.lastEvent = null, 250);

		this.filterText = '';
		this.visibleItems = this.slickDropDownService.getVisibleItems(this.items, this.idFieldName, this.textFieldName);
		this.selectItem(this.selectedId, false);
		(<HTMLInputElement>this.selectedTextInputRef.nativeElement).focus();

		setTimeout(() => (<HTMLInputElement>this.selectedTextInputRef.nativeElement).select(), 0);

		if (this.expanded === false)
			this.expand();
		else
			this.collapse();
	}

	onItemClick(item: any) {
		this.selectItem(item.id, true);
		this.collapse();
	}

	onInput(e: Event) {
		this.filterText = (<HTMLInputElement>e.target).value;

		if (!this.filterText) {
			this.selectedIndex = null;
			this.visibleItems = this.slickDropDownService.getVisibleItems(this.items, this.idFieldName, this.textFieldName);
			return;
		}

		this.filterSearch();
	}

	// ---- phone picker ----

	private openPicker() {
		if (this.disabled || this.pickerOpen)
			return;

		// iOS doesn't move focus to a tapped non-input, so a box typed into just before would keep the
		// keyboard up over the list
		(<HTMLElement>document.activeElement)?.blur?.();

		this.filterText = '';
		this.visibleItems = this.slickDropDownService.getVisibleItems(this.items, this.idFieldName, this.textFieldName);
		this.pickerOpen = true;
		this.changeDetector.detectChanges();
		SlickUtilsService.attachElement(this.mobilePickerRef.nativeElement, 'body');

		// Open on the current choice rather than the top of a long list
		const list: HTMLElement = this.mobilePickerListRef?.nativeElement;
		const selected = <HTMLElement>list?.querySelector(".selected");
		if (list && selected)
			list.scrollTop = selected.offsetTop - (list.clientHeight - selected.offsetHeight) / 2;

		if (this.onExpand)
			this.onExpand.emit();
	}

	closePicker() {
		if (!this.pickerOpen)
			return;

		SlickUtilsService.removeElement(this.mobilePickerRef.nativeElement);
		this.pickerOpen = false;
		this.filterText = '';
		this.visibleItems = this.slickDropDownService.getVisibleItems(this.items, this.idFieldName, this.textFieldName);
	}

	onPickerSearch() {
		this.applyFilter();
	}

	// Same as picking from the typed list: onSelect fires even when the choice didn't change
	onPickerItemClick(item: any) {
		this.selectItem(item.id, true);
		this.closePicker();
	}
}